import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type MouseEvent, useMemo, useState } from 'react'
import { TableVirtuoso, type TableComponents } from 'react-virtuoso'
import { type SettingLogHistoryItem, useGetSettingLogHistoryQuery } from '../../store/api/settingsApi'
import BrandState from '../../components/BrandState'
import DateTextField from '../../components/DateTextField'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '../../components/dataList'
import {
  DATE_FILTER_COMPACT,
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../theme/responsive'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import '../../styles/app-shared.css'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'

/* ---------- helpers -------------------------------------------------- */

const todayStr = () => new Date().toISOString().slice(0, 10)

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  const ss = `${d.getSeconds()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}:${ss}`
}

/* ---------- style constants ----------------------------- */

const headerButtonSx = {
  minHeight: 28,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

/* ---------- column definitions (Access §2.3 order) --------------------- */

interface ColDef {
  key: keyof SettingLogHistoryItem
  label: string
  minWidth: number
  align?: 'left' | 'right' | 'center'
  multiline?: boolean
  kind?: 'text' | 'datetime'
  filter?: ColumnFilterKind
}

const WIDTHS_TABLE_KEY = 'settings.log-history'

const ALL_COLUMNS: ColDef[] = [
  { key: 'personUpdated', label: 'Person updated',  minWidth: 200, filter: 'checkboxes' },
  { key: 'dateUpdated',   label: 'Date updated',    minWidth: 148, kind: 'datetime' },
  { key: 'rowUpdated',    label: 'Record',          minWidth: 280, multiline: true },
]

/* ---------- Virtuoso table components --------------------------------- */

const VirtuosoTableComponents: TableComponents<SettingLogHistoryItem> = {
  Scroller: ({ children, ...props }) => (
    <Box
      component="div"
      {...props}
      sx={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}
    >
      {children}
    </Box>
  ),
  Table: ({ children, style }) => (
    <Table
      className="app-table-wrap"
      size="small"
      stickyHeader
      style={{ ...style, borderCollapse: 'separate', tableLayout: 'auto' }}
    >
      {children}
    </Table>
  ),
  TableHead: ({ children, ...props }) => <TableHead {...props}>{children}</TableHead>,
  TableRow: ({ children, ...props }) => <TableRow hover {...props}>{children}</TableRow>,
  TableBody: ({ children, ...props }) => <TableBody {...props}>{children}</TableBody>,
}

/* ---------- component ------------------------------------------------- */

export default function SettingsLogHistoryView() {
  const [dateStr, setDateStr] = useState(todayStr)
  const [fetchDate, setFetchDate] = useState(todayStr)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [sortState, setSortState] = useState<TableSortState<keyof SettingLogHistoryItem> | null>(null)
  const { getWidth, startResize } = useColumnWidths(WIDTHS_TABLE_KEY)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SettingLogHistoryItem
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const columnsCtrl = usePreferenceColumns<ColDef>({
    scope: 'settings',
    prefKey: 'columns',
    subKey: 'log_history',
    allColumns: ALL_COLUMNS,
    widthsTableKey: WIDTHS_TABLE_KEY,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error, refetch } = useGetSettingLogHistoryQuery(fetchDate)

  const rows = useMemo(() => data?.items ?? [], [data])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, ALL_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [rows, columnFilters.filters, searchTerm],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, visibleColumns),
    [filteredRows, sortState, visibleColumns],
  )
  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    return sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const popoverColumn = useMemo<ColDef | null>(() => {
    if (!colPopover) return null
    return ALL_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text',
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const handleDateChange = (v: string) => {
    setDateStr(v)
    setFetchDate(v)
    setPage(0)
  }

  const handleRefresh = () => {
    setFetchDate(dateStr)
    setPage(0)
    refetch()
  }

  const toggleSort = (key: keyof SettingLogHistoryItem) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const renderCell = (row: SettingLogHistoryItem, col: ColDef) => {
    const raw = row[col.key]
    const w = getWidth(col.key as string)
    const widthStyle = w ? { width: w, minWidth: w, maxWidth: w } : undefined
    if (col.key === 'dateUpdated') {
      const val = formatAccessDate(raw)
      return (
        <TableCell key={col.key} style={widthStyle} sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {val}
        </TableCell>
      )
    }
    if (col.multiline && raw) {
      return (
        <TableCell key={col.key} style={widthStyle} sx={{ py: 0.5, maxWidth: w ?? 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Box
            sx={{
              maxHeight: 72,
              overflowY: 'auto',
              fontSize: '0.72rem',
              lineHeight: 1.4,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {raw}
          </Box>
        </TableCell>
      )
    }
    const val = raw ?? '-'
    return (
      <TableCell key={col.key} align={col.align} style={widthStyle} sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <Box
          component="span"
          className="app-table-cell-text"
          title={String(val)}
          sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {val || '-'}
        </Box>
      </TableCell>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                whiteSpace: 'nowrap',
                fontSize: '0.8rem',
                ...HIDE_ON_MOBILE_INLINE,
              }}
            >
              Snapshot Date:
            </Typography>
            <DateTextField
              size="small"
              value={dateStr}
              onChange={handleDateChange}
              sx={{ width: { xs: 130, sm: 150 }, ...DATE_FILTER_COMPACT }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleRefresh}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon sx={{ fontSize: 14 }} />}
              sx={headerButtonSx}
              title="Refresh"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Refresh</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e: MouseEvent<HTMLElement>) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
        maxWidth={320}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Person, record..."
            label="Search log history"
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={closeColPopover}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0 }}>
          {/* Pagination toolbar */}
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
              minHeight: 30,
              py: 0.125,
            }}
          >
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Box>

          {/* Table */}
          <Box sx={{ flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' }}>
            {error ? (
              <BrandState
                tone="error"
                title="Could not load Log History"
                description="Please refresh the page or contact the system administrator if the issue continues."
              />
            ) : isLoading && filteredRows.length === 0 ? (
              <BrandState tone="loading" title="Loading…" description="Please wait." />
            ) : pagedRows.length === 0 ? (
              <BrandState title="No log entries found" description="Try adjusting the date or search filter above." />
            ) : (
            <TableVirtuoso
              data={pagedRows}
              components={VirtuosoTableComponents}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    className="app-table-header-cell"
                    sx={{ whiteSpace: 'nowrap', py: 0.5, width: 40, minWidth: 40, fontSize: '0.72rem' }}
                  >
                    #
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const w = getWidth(col.key as string)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', py: 0.5, minWidth: col.minWidth, cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                          <Box className="app-table-head-text">
                            {col.label}
                            {getTableSortSuffix(sortState, col.key)}
                          </Box>
                          <ColumnFilterIcon
                            active={columnFilters.isActive(String(col.key))}
                            onClick={(position) => setColPopover({ key: col.key, position })}
                            ariaLabel={`Filter ${col.label}`}
                          />
                        </Box>
                        <ResizeHandle onMouseDown={(e) => startResize(col.key as string, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(index, row) => (
                <>
                  <TableCell sx={{ whiteSpace: 'nowrap', py: 0.5, color: 'text.secondary', fontSize: '0.68rem' }}>
                    {(rowsPerPage === -1 ? 0 : page * rowsPerPage) + index + 1}
                  </TableCell>
                  {visibleColumns.map((col) => renderCell(row, col))}
                </>
              )}
            />
            )}
          </Box>
        </Box>
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
